/**
 * Notification Provider Component
 *
 * A context provider that manages notification display with auto-dismiss functionality.
 * Provides a floating notification overlay that appears at the top of the screen.
 * Uses the crop-circle Notification component for consistent styling.
 */

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { IconName } from '../crop-circle/icons';

interface NotificationData {
  id: string;
  title: string;
  text?: string;
  style: 'success' | 'warning' | 'error' | 'neutral' | 'info';
  leftIcon?: IconName;
  showLeftIcon?: boolean;
  duration?: number;
}

interface NotificationContextValue {
  notifications: NotificationData[];  
  showNotification: (notification: Omit<NotificationData, 'id'>) => void;
  showSuccess: (title: string, text?: string, duration?: number, leftIcon?: IconName) => void;
  showError: (title: string, text?: string, duration?: number, leftIcon?: IconName) => void;
  showWarning: (title: string, text?: string, duration?: number, leftIcon?: IconName) => void;
  showInfo: (title: string, text?: string, duration?: number, leftIcon?: IconName) => void;
  hideNotification: () => void;
}

const SystemNotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useSystemNotification = () => {
  const context = useContext(SystemNotificationContext);
  if (!context) {
    throw new Error('useSystemNotification must be used within a SystemNotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const SystemNotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const timeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const showNotificationDuration = 2000;

  const removeItem = useCallback((id?: string) => {
    if (id) {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } else {
      setNotifications([]);
    }
  }, []);

  const showNotification = useCallback((notificationData: Omit<NotificationData, 'id'>) => {
    const newNotification: NotificationData = {
      id: Date.now().toString(),
      ...notificationData,
    };

    // Small delay to ensure previous notification is hidden
    setNotifications(prev => [
      ...prev,
      newNotification
    ]);

    // Auto-hide after duration - store individual timeout
    const timeout = setTimeout(() => {
      removeItem(newNotification.id);

      timeoutRef.current.delete(newNotification.id);
    }, notificationData.duration || showNotificationDuration);
    
    timeoutRef.current.set(newNotification.id, timeout);
  }, [removeItem]);

  const hideNotification = useCallback(() => {
    // Clear all timeouts
    timeoutRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutRef.current.clear();
    removeItem();
  }, [removeItem]);

  const showSuccess = useCallback((title: string, text?: string, duration?: number, leftIcon?: IconName) => {
    showNotification({
      title,
      text,
      style: 'success',
      leftIcon,
      showLeftIcon: leftIcon ? true : false,
      duration,
    });
  }, [showNotification]);

  const showError = useCallback((title: string, text?: string, duration?: number, leftIcon?: IconName) => {
    showNotification({
      title,
      text,
      style: 'error',
      leftIcon,
      showLeftIcon: leftIcon ? true : false,
      duration,
    });
  }, [showNotification]);

  const showWarning = useCallback((title: string, text?: string, duration?: number, leftIcon?: IconName) => {
    showNotification({
      title,
      text,
      style: 'warning',
      leftIcon,
      showLeftIcon: leftIcon ? true : false,
      duration,
    });
  }, [showNotification]);

  const showInfo = useCallback((title: string, text?: string, duration?: number, leftIcon?: IconName) => {
    showNotification({
      title,
      text,
      style: 'info',
      leftIcon,
      showLeftIcon: leftIcon ? true : false,
      duration,
    });
  }, [showNotification]);

  const contextValue: NotificationContextValue = {
    notifications,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification,
  };

  return (
    <SystemNotificationContext.Provider value={contextValue}>
      {children}
    </SystemNotificationContext.Provider>
  );
};

export default SystemNotificationProvider;